import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { MapPin, Clock, Users, ArrowLeft, Check, X, UserPlus, Share2, Zap, MessageCircle, MoreVertical, Flag, Edit3, Calendar, Trash2, Pause, Play, DollarSign, Store, Tag, Plus, Repeat } from 'lucide-react';

const COST_LABELS_DETAILS = {
  free: 'Free',
  individual: 'Pay individually',
  split: 'Split bill',
  fixed: 'Fixed amount',
  host_pays: 'Host pays',
};
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import ReportModal from '../components/ReportModal';
import VenueInviteSheet from '../components/VenueInviteSheet';
import CommunityCreateSheet from '../components/CommunityCreateSheet';
import AddToCalendarSheet from '../components/AddToCalendarSheet';
import ConflictModal from '../components/ConflictModal';
import { RichOfferCard } from '../components/VenuePartnerPicker';
import { RecurringBadge } from '../components/RecurrencePicker';
import { Users as UsersIcon, Sparkles, X as XIcon } from 'lucide-react';

function getInitials(name) {
  return (name || '?').split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
}

function TrustBadge({ level }) {
  if (!level || level === 'new_member') return null;
  const labels = { trusted_host: 'Trusted Host', community_favorite: 'Community Favorite', reliable_member: 'Reliable' };
  return <span className={`trust-badge ${level}`}>{labels[level] || level}</span>;
}

export default function PlanDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);
  const [showParticipants, setShowParticipants] = useState(false);
  const [showHostMenu, setShowHostMenu] = useState(false);
  const [showReport, setShowReport] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
  const [cancelReason, setCancelReason] = useState('other');
  const [chatUnread, setChatUnread] = useState(0);
  const [showInviteVenue, setShowInviteVenue] = useState(false);
  const [showCommunityPrompt, setShowCommunityPrompt] = useState(false);
  const [communityPrompt, setCommunityPrompt] = useState(null);
  const [showAddToCal, setShowAddToCal] = useState(false);
  const [conflicts, setConflicts] = useState([]);
  const [showConflict, setShowConflict] = useState(false);

  useEffect(() => {
    const fetch = async () => {
      try {
        const { data } = await api.get(`/plans/${id}`);
        setPlan(data);
        // Log a view — feeds the discovery engine's category affinity
        // signals. Fire-and-forget so a failure here never blocks render.
        api.post(`/discovery/view/${id}`).catch(() => {});
        // Q28 — should we prompt the host to keep this community alive?
        if (data.is_host) {
          api.get(`/communities/promote-prompt/${id}`)
            .then(r => { if (r.data?.prompt) setCommunityPrompt(r.data.plan); })
            .catch(() => {});
        }
      } catch { navigate('/'); }
      setLoading(false);
    };
    fetch();
  }, [id, navigate]);

  useEffect(() => {
    if (!plan) return;
    const isHost = plan.host_id === currentUser?.id;
    if (!isHost && plan.user_status !== 'approved') return;
    const fetchUnread = async () => {
      try {
        const { data } = await api.get(`/plans/${id}/chat/unread`);
        setChatUnread(data.unread || 0);
      } catch {}
    };
    fetchUnread();
    const interval = setInterval(fetchUnread, 15000);
    return () => clearInterval(interval);
  }, [plan, id, currentUser]);

  const handleJoin = async () => {
    setJoining(true);
    try {
      const { data } = await api.post(`/plans/${id}/join`);
      setPlan(p => ({ ...p, user_status: data.status }));
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to join');
    }
    setJoining(false);
  };

  // Q32 Phase B — Pre-flight conflict check. Falls back to direct join on
  // any error so we never block the user because of a flaky calendar API.
  const attemptJoin = async () => {
    try {
      const { data } = await api.get(`/calendar/conflicts/${id}`);
      if ((data?.conflicts || []).length > 0) {
        setConflicts(data.conflicts);
        setShowConflict(true);
        return;
      }
    } catch { /* swallow — proceed with join */ }
    handleJoin();
  };

  const confirmJoinDespiteConflict = () => {
    setShowConflict(false);
    handleJoin();
  };

  const handleLeave = async () => {
    try {
      await api.delete(`/plans/${id}/leave`);
      setPlan(p => ({ ...p, user_status: null }));
    } catch {}
  };

  const handleParticipantAction = async (userId, action) => {
    try {
      await api.put(`/plans/${id}/participants/${userId}?action=${action}`);
      const { data } = await api.get(`/plans/${id}`);
      setPlan(data);
    } catch {}
  };

  const handleCancelPlan = async () => {
    try {
      await api.post(`/plans/${id}/cancel`, { reason: cancelReason });
      setPlan(p => ({ ...p, status: 'cancelled' }));
      setShowCancelConfirm(false);
      setShowHostMenu(false);
    } catch (err) { alert(err.response?.data?.detail || 'Failed'); }
  };

  const handleDeletePlan = async () => {
    try {
      await api.delete(`/plans/${id}`);
      navigate('/');
    } catch (err) { alert(err.response?.data?.detail || 'Cannot delete plan with attendees. Cancel instead.'); }
    setShowDeleteConfirm(false);
  };

  const handleCloseSpots = async () => {
    try {
      const { data } = await api.post(`/plans/${id}/close-spots`);
      setPlan(p => ({ ...p, join_paused: data.join_paused }));
      setShowHostMenu(false);
    } catch {}
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
  };

  if (loading) {
    return (
      <div className="content-area px-4 pt-4">
        <div className="skeleton h-[250px] rounded-3xl mb-4" />
        <div className="skeleton h-8 w-3/4 mb-3" />
        <div className="skeleton h-4 w-1/2 mb-2" />
        <div className="skeleton h-4 w-2/3" />
      </div>
    );
  }

  if (!plan) return null;

  const spotsLeft = plan.spots_left ?? (plan.capacity - (plan.approved_count || 0));
  const isHost = plan.is_host;

  return (
    <div className="content-area animate-fade-in" data-testid="plan-details-page">
      {/* Hero */}
      <div className="relative">
        {plan.cover_image ? (
          <img loading="lazy" decoding="async" src={plan.cover_image} alt={plan.title} className="w-full h-[280px] object-cover" />
        ) : (
          <div className="w-full h-[280px] bg-gradient-to-br from-[#F1F0EA] to-[#0A0A0A] flex items-center justify-center">
            <Zap size={48} className="text-[#999]" />
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-[#F7F6F2] via-[#F7F6F2]/40 to-transparent" />
        <button
          data-testid="back-btn"
          onClick={() => navigate(-1)}
          className="absolute top-4 left-4 bg-white/70 backdrop-blur-lg rounded-full p-2.5"
        >
          <ArrowLeft size={20} className="text-[#111111]" />
        </button>
        <div className="absolute top-4 right-4 flex gap-2">
          {!isHost && (
            <button data-testid="report-plan-btn" onClick={() => setShowReport(true)} className="bg-white/70 backdrop-blur-lg rounded-full p-2.5">
              <Flag size={18} className="text-[#9B9EA3]" />
            </button>
          )}
          {isHost && (
            <button data-testid="host-menu-btn" onClick={() => setShowHostMenu(!showHostMenu)} className="bg-white/70 backdrop-blur-lg rounded-full p-2.5">
              <MoreVertical size={18} className="text-[#111111]" />
            </button>
          )}
        </div>
        {/* Host Menu Dropdown */}
        {showHostMenu && isHost && (
          <div className="absolute top-16 right-4 bg-white rounded-2xl shadow-xl shadow-black/10 border border-black/[0.06] py-2 min-w-[200px] z-20 animate-scale-in" data-testid="host-menu">
            <button onClick={() => { navigate(`/create?edit=${id}`); setShowHostMenu(false); }} className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-[#111111] hover:bg-[#F1F0EA] transition-colors">
              <Edit3 size={16} /> Edit Plan
            </button>
            <button onClick={() => { setShowCancelConfirm(true); setShowHostMenu(false); }} className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-[#111111] hover:bg-[#F1F0EA] transition-colors">
              <X size={16} /> Cancel Plan
            </button>
            <button onClick={handleCloseSpots} className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-[#111111] hover:bg-[#F1F0EA] transition-colors">
              {plan?.join_paused ? <Play size={16} /> : <Pause size={16} />}
              {plan?.join_paused ? 'Open Spots' : 'Close Spots'}
            </button>
            <button onClick={() => { setShowDeleteConfirm(true); setShowHostMenu(false); }} className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-[#FF3B30] hover:bg-red-50 transition-colors">
              <Trash2 size={16} /> Cancel plan (no attendees)
            </button>
          </div>
        )}
      </div>

      <div className="px-4 -mt-12 relative z-10">
        {/* Title + Status */}
        <h1 className="text-2xl font-black tracking-tight text-[#111111] leading-tight">{plan.title}</h1>
        {plan.recurrence?.frequency && (
          <div className="mt-2"><RecurringBadge plan={plan} /></div>
        )}

        {communityPrompt && (
          <div data-testid="community-prompt-banner" className="mt-3 bg-[#B6E600]/15 border border-[#B6E600]/35 rounded-2xl p-4 flex items-start gap-3">
            <div className="w-10 h-10 rounded-2xl bg-[#B6E600] text-[#111] flex items-center justify-center flex-shrink-0">
              <Sparkles size={18} />
            </div>
            <div className="flex-1">
              <p className="text-sm font-extrabold text-[#111] leading-snug">Keep this community alive?</p>
              <p className="text-xs text-[#3F5200] mt-1 leading-relaxed">
                Promote this plan into a permanent community with persistent chat
                and recurring events.
              </p>
              <div className="mt-3 flex gap-2">
                <button
                  data-testid="community-prompt-accept"
                  onClick={() => setShowCommunityPrompt(true)}
                  className="bg-[#111] text-white text-xs font-bold rounded-full px-3 py-1.5"
                >
                  Yes, let's do it
                </button>
                <button
                  data-testid="community-prompt-dismiss"
                  onClick={async () => {
                    try { await api.post(`/communities/promote-prompt/${id}/dismiss`); } catch {}
                    setCommunityPrompt(null);
                  }}
                  className="bg-white border border-black/[0.08] text-[#6B6E73] text-xs font-bold rounded-full px-3 py-1.5"
                >
                  Not now
                </button>
              </div>
            </div>
            <button
              data-testid="community-prompt-close"
              onClick={async () => {
                try { await api.post(`/communities/promote-prompt/${id}/dismiss`); } catch {}
                setCommunityPrompt(null);
              }}
              className="text-[#9B9EA3] flex-shrink-0"
              aria-label="Dismiss"
            >
              <XIcon size={16} />
            </button>
          </div>
        )}

        {plan.community_id && !communityPrompt && (
          <button
            data-testid="plan-community-chip"
            onClick={() => navigate(`/community/${plan.community_id}`)}
            className="mt-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#F1F0EA] text-[#111] text-[11px] font-bold border border-black/[0.06] active:bg-[#E3E3DA]"
          >
            <UsersIcon size={11} /> Part of a community
          </button>
        )}

        {/* Host Card */}
        <div
          className="flex items-center gap-3 mt-4 p-3 bg-[#FFFFFF] rounded-2xl border border-black/[0.06] cursor-pointer"
          onClick={() => navigate(`/user/${plan.host?.id}`)}
          data-testid="host-card"
        >
          {plan.host?.profile_photo ? (
            <img loading="lazy" decoding="async" src={plan.host.profile_photo} alt="" className="w-11 h-11 rounded-full object-cover" />
          ) : (
            <div className="w-11 h-11 rounded-full bg-[#F1F0EA] flex items-center justify-center text-sm font-bold">
              {getInitials(plan.host?.display_name)}
            </div>
          )}
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <span className="font-semibold text-sm text-[#111111]">{plan.host?.display_name}</span>
              <TrustBadge level={plan.host?.trust_level} />
            </div>
            <span className="text-xs text-[#9B9EA3]">@{plan.host?.username} &middot; {plan.host?.hosted_count || 0} hosted</span>
          </div>
        </div>

        {/* Meta */}
        <div className="mt-5 space-y-3">
          <div className="flex items-center gap-3 text-sm">
            <Clock size={18} className="text-[#B6E600]" />
            <div>
              <p className="text-[#111111] font-medium">{formatDate(plan.date)}</p>
              <p className="text-xs text-[#6B6E73]">{plan.start_time}{plan.end_time ? ` - ${plan.end_time}` : ''}</p>
            </div>
          </div>
          <div className="flex items-center gap-3 text-sm">
            <MapPin size={18} className="text-[#B6E600]" />
            <div>
              <p className="text-[#111111] font-medium">{plan.venue || plan.city}</p>
              <p className="text-xs text-[#6B6E73]">{[plan.area, plan.city].filter(Boolean).join(', ')}</p>
            </div>
          </div>
          <div className="flex items-center gap-3 text-sm">
            <Users size={18} className="text-[#B6E600]" />
            <div>
              <p className="text-[#111111] font-medium">
                {plan.approved_count || 0} going &middot; {spotsLeft > 0 ? `${spotsLeft} spots left` : 'Full'}
              </p>
              <p className="text-xs text-[#6B6E73] capitalize">{plan.join_mode === 'request' ? 'Request to join' : 'Open to join'}</p>
            </div>
          </div>
          {plan.cost_type && plan.cost_type !== 'unknown' && (
            <div className="flex items-center gap-3 text-sm" data-testid="plan-cost-row">
              <DollarSign size={18} className="text-[#B6E600]" />
              <div>
                <p className="text-[#111111] font-medium">
                  {COST_LABELS_DETAILS[plan.cost_type] || 'Cost'}
                  {plan.cost_amount ? ` · ${plan.cost_amount}` : ''}
                </p>
                <p className="text-xs text-[#9B9EA3]">ASAP doesn't handle payments — sort it among yourselves.</p>
              </div>
            </div>
          )}
        </div>

        {/* Partner venue (Q25) + active offers */}
        {plan.business && plan.business.status === 'active' ? (
          <div data-testid="plan-partner-venue" className="mt-4 bg-white rounded-2xl border border-[#B6E600]/35 p-4 cursor-pointer active:bg-[#F1F0EA]" onClick={() => navigate(`/business/${plan.business.id}`)}>
            <div className="w-full flex items-center gap-3">
              <div className="w-10 h-10 rounded-2xl bg-[#B6E600]/15 text-[#3F5200] flex items-center justify-center">
                <Store size={18} />
              </div>
              <div className="flex-1 text-left min-w-0">
                <p className="text-[10px] font-bold uppercase tracking-wider text-[#3F5200]">Partner venue</p>
                <p className="text-sm font-extrabold text-[#111] truncate">{plan.business.name}</p>
              </div>
            </div>
            {plan.business.offers?.length > 0 && (
              <div className="mt-3 space-y-2" data-testid="plan-partner-offers">
                {plan.business.offers.map(o => (
                  <div key={o.id} data-testid={`plan-offer-${o.id}`}>
                    <RichOfferCard offer={o} venueName={plan.business.name} />
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          plan.venue && currentUser && currentUser.id !== plan.host_id ? null : (
            plan.venue && (
              <button
                type="button"
                data-testid="plan-invite-venue-btn"
                onClick={() => setShowInviteVenue(true)}
                className="mt-4 w-full flex items-center gap-3 bg-white rounded-2xl border border-dashed border-black/[0.12] p-4 active:bg-[#F1F0EA] text-left"
              >
                <div className="w-9 h-9 rounded-2xl bg-[#F1F0EA] text-[#3F5200] flex items-center justify-center">
                  <Plus size={16} />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-bold text-[#111]">Invite {plan.venue} to ASAP</p>
                  <p className="text-[11px] text-[#9B9EA3] mt-0.5">Earn points when they join + create offers for your groups</p>
                </div>
              </button>
            )
          )
        )}

        {/* Description */}
        {plan.description && (
          <div className="mt-5">
            <p className="text-sm text-[#2A2A2A] leading-relaxed whitespace-pre-wrap">{plan.description}</p>
          </div>
        )}

        {/* Hashtags */}
        {plan.hashtags?.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-4">
            {plan.hashtags.map(tag => (
              <span
                key={tag}
                className="hashtag text-sm cursor-pointer"
                onClick={() => navigate(`/hashtag/${tag}`)}
              >
                #{tag}
              </span>
            ))}
          </div>
        )}

        {/* Attendees Preview */}
        {plan.participants?.approved?.length > 0 && (
          <div className="mt-5">
            <button
              data-testid="show-participants"
              onClick={() => setShowParticipants(!showParticipants)}
              className="flex items-center gap-2 mb-3"
            >
              <div className="flex -space-x-2.5">
                {plan.participants.approved.slice(0, 5).map((a, i) => (
                  a.profile_photo ? (
                    <img loading="lazy" decoding="async" key={i} src={a.profile_photo} alt="" className="w-8 h-8 rounded-full object-cover border-2 border-[#F7F6F2]" />
                  ) : (
                    <div key={i} className="w-8 h-8 rounded-full bg-[#F1F0EA] border-2 border-[#F7F6F2] flex items-center justify-center text-[9px] font-bold">
                      {getInitials(a.display_name)}
                    </div>
                  )
                ))}
              </div>
              <span className="text-xs text-[#6B6E73]">{plan.participants.approved.length} going</span>
            </button>
          </div>
        )}

        {/* Host: Pending Requests */}
        {isHost && plan.participants?.pending?.length > 0 && (
          <div className="mt-5 p-4 bg-[#FFFFFF] rounded-2xl border border-black/[0.06]">
            <h3 className="text-sm font-bold text-[#111111] mb-3">Pending Requests ({plan.participants.pending.length})</h3>
            <div className="space-y-3">
              {plan.participants.pending.map(p => (
                <div key={p.id} className="flex items-center gap-3">
                  {p.profile_photo ? (
                    <img loading="lazy" decoding="async" src={p.profile_photo} alt="" className="w-10 h-10 rounded-full object-cover" />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-[#F1F0EA] flex items-center justify-center text-xs font-bold">
                      {getInitials(p.display_name)}
                    </div>
                  )}
                  <div className="flex-1">
                    <p className="text-sm font-medium text-[#111111]">{p.display_name}</p>
                    <p className="text-[11px] text-[#9B9EA3]">@{p.username}</p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      data-testid={`approve-${p.id}`}
                      onClick={() => handleParticipantAction(p.id, 'approve')}
                      className="bg-[#111111] text-white rounded-full p-1.5 active:scale-95"
                    >
                      <Check size={16} />
                    </button>
                    <button
                      data-testid={`decline-${p.id}`}
                      onClick={() => handleParticipantAction(p.id, 'decline')}
                      className="bg-[#F1F0EA] text-[#9B9EA3] rounded-full p-1.5 border border-black/[0.08] active:scale-95"
                    >
                      <X size={16} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Join CTA */}
        <div className="mt-6 mb-8 pb-24">
          {isHost ? (
            <div className="space-y-2">
              <div className="bg-[#FFFFFF] rounded-2xl p-4 border border-[#B6E600]/20 text-center">
                <p className="text-sm text-[#B6E600] font-medium">You're hosting this plan</p>
              </div>
              <button
                data-testid="host-chat-btn"
                onClick={() => navigate(`/plan/${id}/chat`)}
                className="w-full bg-[#F1F0EA] text-[#111111] font-medium rounded-full py-3 text-sm flex items-center justify-center gap-2 border border-black/[0.06] active:scale-[0.98] transition-transform"
              >
                <MessageCircle size={18} /> Group Chat
                {chatUnread > 0 && (
                  <span data-testid="host-chat-unread" className="min-w-[20px] h-5 px-1.5 rounded-full bg-[#FF3B30] text-white text-[10px] font-bold flex items-center justify-center ml-1">
                    {chatUnread > 99 ? '99+' : chatUnread}
                  </span>
                )}
              </button>
              <AddToCalendarButton
                onClick={() => setShowAddToCal(true)}
                recurring={plan.is_recurring}
              />
            </div>
          ) : plan.user_status === 'approved' ? (
            <div className="space-y-2">
              <div className="bg-[#B6E600]/10 rounded-2xl p-4 text-center border border-[#B6E600]/20">
                <p className="text-sm text-[#B6E600] font-medium flex items-center justify-center gap-2">
                  <Check size={18} /> You're going!
                </p>
              </div>
              <button
                data-testid="attendee-chat-btn"
                onClick={() => navigate(`/plan/${id}/chat`)}
                className="w-full bg-[#F1F0EA] text-[#111111] font-medium rounded-full py-3 text-sm flex items-center justify-center gap-2 border border-black/[0.06] active:scale-[0.98] transition-transform"
              >
                <MessageCircle size={18} /> Group Chat
                {chatUnread > 0 && (
                  <span data-testid="attendee-chat-unread" className="min-w-[20px] h-5 px-1.5 rounded-full bg-[#FF3B30] text-white text-[10px] font-bold flex items-center justify-center ml-1">
                    {chatUnread > 99 ? '99+' : chatUnread}
                  </span>
                )}
              </button>
              <AddToCalendarButton
                onClick={() => setShowAddToCal(true)}
                recurring={plan.is_recurring}
              />
              <button
                data-testid="leave-plan-btn"
                onClick={() => setShowLeaveConfirm(true)}
                className="w-full bg-white text-[#FF3B30] font-medium rounded-full py-3 text-sm border border-[#FF3B30]/30 active:scale-[0.98] transition-all active:bg-red-50"
              >
                Leave Plan
              </button>
            </div>
          ) : plan.user_status === 'pending' ? (
            <div className="space-y-2">
              <div className="bg-[#F1F0EA] rounded-2xl p-4 text-center border border-black/[0.06]">
                <p className="text-sm text-[#6B6E73] font-medium">Request pending</p>
                <button
                  data-testid="cancel-request-btn"
                  onClick={handleLeave}
                  className="text-xs text-[#9B9EA3] mt-2 active:text-[#FF3B30]"
                >
                  Cancel request
                </button>
              </div>
            </div>
          ) : plan.user_status === 'waitlisted' ? (
            <div className="space-y-2">
              <div className="bg-[#F1F0EA] rounded-2xl p-4 text-center border border-black/[0.06]">
                <p className="text-sm text-[#6B6E73] font-medium">You're on the waitlist</p>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              <button
                data-testid="join-plan-btn"
                onClick={attemptJoin}
                disabled={joining}
                className="w-full bg-[#B6E600] text-[#111111] font-bold rounded-full py-4 text-sm flex items-center justify-center gap-2 active:scale-[0.98] transition-all disabled:opacity-50 shadow-[0_0_20px_rgba(212,255,0,0.2)]"
              >
                {joining ? 'Joining...'
                  : spotsLeft <= 0 ? 'Join Waitlist'
                  : plan.join_mode === 'request' ? 'Request to Join'
                  : 'Join Plan'}
                {!joining && <UserPlus size={18} />}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Leave Plan Confirmation — centered dialog */}
      {showLeaveConfirm && (
        <div
          className="fixed inset-0 z-[10001] flex items-center justify-center px-6 bg-black/50 backdrop-blur-[2px] animate-fade-in"
          onClick={() => setShowLeaveConfirm(false)}
        >
          <div
            className="w-full max-w-[360px] bg-white rounded-3xl shadow-2xl p-6 animate-scale-in"
            onClick={e => e.stopPropagation()}
            data-testid="leave-confirm-modal"
          >
            <h3 className="text-lg font-bold text-[#111111] mb-2">Leave this plan?</h3>
            <p className="text-sm text-[#6B6E73] mb-5">You'll lose your reserved spot.</p>
            <div className="flex flex-col gap-2">
              <button
                data-testid="confirm-leave-btn"
                onClick={() => { handleLeave(); setShowLeaveConfirm(false); }}
                className="w-full bg-[#FF3B30] text-white font-bold rounded-full text-sm active:scale-[0.98]"
                style={{ minHeight: 54 }}
              >
                Leave plan
              </button>
              <button
                onClick={() => setShowLeaveConfirm(false)}
                className="w-full bg-transparent text-[#111111] font-semibold rounded-full text-sm active:scale-[0.98]"
                style={{ minHeight: 48 }}
              >
                Keep
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Plan Confirmation — centered dialog (host with attendees) */}
      {showCancelConfirm && (
        <div
          className="fixed inset-0 z-[10001] flex items-center justify-center px-6 bg-black/50 backdrop-blur-[2px] animate-fade-in"
          onClick={() => setShowCancelConfirm(false)}
        >
          <div
            className="w-full max-w-[360px] bg-white rounded-3xl shadow-2xl p-6 animate-scale-in"
            onClick={e => e.stopPropagation()}
            data-testid="cancel-confirm-modal"
          >
            <h3 className="text-lg font-bold text-[#111111] mb-2">Cancel plan?</h3>
            <p className="text-sm text-[#6B6E73] mb-4">All attendees will be notified. This cannot be undone.</p>
            <label className="text-[11px] font-semibold text-[#6B6E73] uppercase tracking-wide block mb-2">Reason</label>
            <select
              value={cancelReason}
              onChange={e => setCancelReason(e.target.value)}
              className="w-full bg-[#F1F0EA] text-[#111111] rounded-xl px-3 py-3 text-sm border border-black/[0.06] mb-5"
              data-testid="cancel-reason"
            >
              <option value="emergency">Emergency</option>
              <option value="sick">Sick</option>
              <option value="weather">Weather</option>
              <option value="venue_issue">Venue Issue</option>
              <option value="personal">Personal</option>
              <option value="other">Other</option>
            </select>
            <div className="flex flex-col gap-2">
              <button
                data-testid="confirm-cancel-btn"
                onClick={handleCancelPlan}
                className="w-full bg-[#FF3B30] text-white font-bold rounded-full text-sm active:scale-[0.98]"
                style={{ minHeight: 54 }}
              >
                Cancel plan
              </button>
              <button
                onClick={() => setShowCancelConfirm(false)}
                className="w-full bg-transparent text-[#111111] font-semibold rounded-full text-sm active:scale-[0.98]"
                style={{ minHeight: 48 }}
              >
                Keep plan
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Plan (no attendees) — centered dialog */}
      {showDeleteConfirm && (
        <div
          className="fixed inset-0 z-[10001] flex items-center justify-center px-6 bg-black/50 backdrop-blur-[2px] animate-fade-in"
          onClick={() => setShowDeleteConfirm(false)}
        >
          <div
            className="w-full max-w-[360px] bg-white rounded-3xl shadow-2xl p-6 animate-scale-in"
            onClick={e => e.stopPropagation()}
            data-testid="delete-confirm-modal"
          >
            <h3 className="text-lg font-bold text-[#111111] mb-2">Cancel plan?</h3>
            <p className="text-sm text-[#6B6E73] mb-5">No one has joined yet, so this will remove the plan entirely.</p>
            <div className="flex flex-col gap-2">
              <button
                data-testid="confirm-delete-btn"
                onClick={handleDeletePlan}
                className="w-full bg-[#FF3B30] text-white font-bold rounded-full text-sm active:scale-[0.98]"
                style={{ minHeight: 54 }}
              >
                Cancel plan
              </button>
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="w-full bg-transparent text-[#111111] font-semibold rounded-full text-sm active:scale-[0.98]"
                style={{ minHeight: 48 }}
              >
                Keep plan
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Report Modal */}
      {showReport && <ReportModal type="plan" targetId={id} onClose={() => setShowReport(false)} />}
      <VenueInviteSheet
        open={showInviteVenue}
        onClose={() => setShowInviteVenue(false)}
        defaultVenueName={plan?.venue || ''}
      />
      <CommunityCreateSheet
        open={showCommunityPrompt}
        onClose={() => setShowCommunityPrompt(false)}
        sourcePlan={communityPrompt}
        onCreated={(c) => {
          setShowCommunityPrompt(false);
          setCommunityPrompt(null);
          navigate(`/community/${c.id}`);
        }}
      />
      <AddToCalendarSheet
        open={showAddToCal}
        onClose={() => setShowAddToCal(false)}
        planId={id}
      />
      <ConflictModal
        open={showConflict}
        conflicts={conflicts}
        onContinue={confirmJoinDespiteConflict}
        onCancel={() => setShowConflict(false)}
      />
    </div>
  );
}

function AddToCalendarButton({ onClick, recurring = false }) {
  return (
    <button
      data-testid={recurring ? 'save-future-occurrences-btn' : 'open-add-to-calendar-btn'}
      onClick={onClick}
      className="w-full bg-white text-[#111111] font-medium rounded-full py-3 text-sm flex items-center justify-center gap-2 border border-black/[0.06] active:scale-[0.98] transition-transform"
    >
      {recurring ? <Repeat size={16} strokeWidth={2.4} /> : <Calendar size={16} />}
      {recurring ? 'Save future occurrences' : 'Add to calendar'}
    </button>
  );
}
